import * as THREE from 'three';
import { ParticleField, glowTexture } from './Particles';
import { nameSprite, speechSprite } from './textures';
import { CharacterModel } from '../characters/CharacterModel';

const hash = (s: string) => { let h = 2166136261; for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619); return (h >>> 0) / 4294967296; };

export interface LanternInfo { id: string; x: number; z: number; text: string; name: string; at: string | number; userId?: string; characterId?: string; memorial?: string | null }
interface Item { info: LanternInfo; mesh: THREE.Group; born: number; dir: THREE.Vector2; seed: number; label?: THREE.Sprite; pos: THREE.Vector3 }

/**
 * Đèn hoa đăng: đèn sen nhỏ có nến, trôi chậm từ chỗ thả theo một hướng ngẫu nhiên, nhấp nhô.
 * Đến gần (< 4.5 m) sẽ thấy lời nhắn + tên người thả. Chỉ 2 PointLight dùng chung (gán cho 2 đèn gần nhất)
 * – số đèn trong cảnh cố định nên không biên dịch lại shader khi thả thêm.
 */
export class RiverLanterns {
  readonly group = new THREE.Group();
  private readonly items = new Map<string, Item>();
  private readonly lights: THREE.PointLight[] = [];
  private readonly petalGeo = new THREE.SphereGeometry(0.09, 8, 6, 0, Math.PI * 2, 0, Math.PI / 2);
  private readonly baseGeo = new THREE.CircleGeometry(0.26, 16);
  private readonly candleGeo = new THREE.CylinderGeometry(0.035, 0.035, 0.1, 8);
  private readonly flameGeo = new THREE.SphereGeometry(0.03, 8, 6);
  private readonly petalM = new THREE.MeshStandardMaterial({ color: '#f6b9c9', emissive: '#ff8aa8', emissiveIntensity: 0.7, roughness: 0.6, side: THREE.DoubleSide });
  private readonly baseM = new THREE.MeshStandardMaterial({ color: '#3f6a34', roughness: 0.8, side: THREE.DoubleSide });
  private readonly candleM = new THREE.MeshStandardMaterial({ color: '#f3e6c8', emissive: '#ffcf8a', emissiveIntensity: 0.5 });
  private readonly flameM = new THREE.MeshBasicMaterial({ color: '#ffd27a', transparent: true, blending: THREE.AdditiveBlending, depthWrite: false });
  private readonly glowM = new THREE.SpriteMaterial({ map: glowTexture(), color: '#ffb36a', transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, opacity: 0.9 });
  heightAt: (x: number, z: number) => number = () => 0;
  /** Ẩn đèn của người bị chặn */
  hidden = new Set<string>();
  bound = 55;

  constructor(parent: THREE.Object3D) {
    parent.add(this.group);
    for (let i = 0; i < 2; i++) { const l = new THREE.PointLight('#ffb36a', 0, 8, 1.6); this.lights.push(l); this.group.add(l); }
  }

  add(info: LanternInfo) {
    if (this.items.has(info.id)) return;
    const g = new THREE.Group();
    const base = new THREE.Mesh(this.baseGeo, this.baseM); base.rotation.x = -Math.PI / 2; g.add(base);
    for (let ring = 0; ring < 2; ring++) for (let i = 0; i < 7; i++) {
      const a = (i / 7) * Math.PI * 2 + ring * 0.45; const r = ring ? 0.08 : 0.15;
      const p = new THREE.Mesh(this.petalGeo, this.petalM); p.scale.set(0.7, 1.7, 0.4); p.position.set(Math.cos(a) * r, 0.03 + ring * 0.03, Math.sin(a) * r);
      p.lookAt(p.position.x * 4, 0.6, p.position.z * 4); g.add(p);
    }
    const candle = new THREE.Mesh(this.candleGeo, this.candleM); candle.position.y = 0.08; g.add(candle);
    const flame = new THREE.Mesh(this.flameGeo, this.flameM); flame.scale.set(1, 2, 1); flame.position.y = 0.17; g.add(flame);
    const glow = new THREE.Sprite(this.glowM); glow.scale.setScalar(0.9); glow.position.y = 0.18; g.add(glow);
    const seed = hash(info.id); const ang = seed * Math.PI * 2;
    const born = typeof info.at === 'number' ? info.at : Date.parse(info.at) || Date.now();
    const pos = new THREE.Vector3(info.x, this.heightAt(info.x, info.z), info.z);
    g.position.copy(pos); this.group.add(g);
    this.items.set(info.id, { info, mesh: g, born, dir: new THREE.Vector2(Math.cos(ang), Math.sin(ang)), seed, pos });
    while (this.items.size > 80) { const first = this.items.keys().next().value as string; this.remove(first); } // giữ tối đa 30 đèn
  }

  remove(id: string) { const it = this.items.get(id); if (!it) return; this.dropLabel(it); it.mesh.removeFromParent(); this.items.delete(id); }
  private dropLabel(it: Item) { if (!it.label) return; it.label.removeFromParent(); it.label.material.map?.dispose(); it.label.material.dispose(); it.label = undefined; }
  /** v0.11: hồ tưởng niệm – đèn không trôi đi xa */
  still = false;
  clear() { for (const id of [...this.items.keys()]) this.remove(id); for (const l of this.lights) l.intensity = 0; }
  count() { return this.items.size; }

  update(_dt: number, t: number, player: THREE.Vector3) {
    const now = Date.now(); const near: Array<{ it: Item; d: number }> = [];
    for (const it of this.items.values()) {
      const visible = !(it.info.userId && this.hidden.has(it.info.userId));
      it.mesh.visible = visible; if (!visible) { this.dropLabel(it); continue; }
      const ageS = Math.max(0, (now - it.born) / 1000);
      const travel = it.info.memorial !== undefined && it.info.memorial !== null || this.still ? Math.min(1.2, ageS * 0.05) : Math.min(18, ageS * 0.22); // trôi chậm, tối đa 18 m rồi dừng (đèn tưởng nhớ: chỉ dập dềnh giữa hồ)
      const wob = Math.sin(t * 0.4 + it.seed * 9) * 0.8;
      let x = it.info.x + it.dir.x * travel - it.dir.y * wob, z = it.info.z + it.dir.y * travel + it.dir.x * wob;
      x = THREE.MathUtils.clamp(x, -this.bound, this.bound); z = THREE.MathUtils.clamp(z, -this.bound, this.bound);
      it.pos.set(x, this.heightAt(x, z) + 0.08 + Math.sin(t * 1.6 + it.seed * 20) * 0.04, z);
      it.mesh.position.copy(it.pos); it.mesh.rotation.y = t * 0.15 + it.seed * 6; it.mesh.rotation.z = Math.sin(t * 1.1 + it.seed * 5) * 0.06;
      const d = Math.hypot(x - player.x, z - player.z); near.push({ it, d });
      if (d < 4.5 && !it.label) { const s = speechSprite(`“${it.info.text}”`, it.info.memorial ? `Tưởng nhớ ${it.info.memorial} · ${it.info.name}` : `Đèn của ${it.info.name}`, '#ffe9c2', 3.4); s.position.y = 0.6; it.mesh.add(s); it.label = s; }
      else if (d > 5.5 && it.label) this.dropLabel(it);
    }
    near.sort((a, b) => a.d - b.d);
    this.lights.forEach((l, i) => { const n = near[i]; if (n && n.d < 25) { l.position.copy(n.it.pos).setY(n.it.pos.y + 0.3); l.intensity = 3.2 + Math.sin(t * 9 + i) * 0.4; } else l.intensity = 0; });
  }

  dispose() { this.clear(); this.group.removeFromParent(); [this.petalGeo, this.baseGeo, this.candleGeo, this.flameGeo, this.petalM, this.baseM, this.candleM, this.flameM, this.glowM].forEach((x) => x.dispose()); }
}

/**
 * Linh hồn lạc lối (việc mỗi ngày): bóng người mờ xanh trôi lơ lửng quanh một điểm.
 * Được giúp ⇒ cúi chào, sáng lên rồi bay lên tan biến.
 */
export class LostSoul {
  readonly model: CharacterModel;
  private readonly mat = new THREE.MeshStandardMaterial({ color: '#9fc8ff', emissive: '#4f8fff', emissiveIntensity: 0.6, transparent: true, opacity: 0.55, depthWrite: false });
  private readonly sparks: ParticleField;
  private t = 0; private releasing = -1;
  readonly home: THREE.Vector3;
  done = false;
  constructor(parent: THREE.Object3D, x: number, y: number, z: number) {
    this.home = new THREE.Vector3(x, y, z);
    this.model = new CharacterModel({ appearance: { hairStyle: 'xoa_dai', hairColor: '#9a9089', face: 'hien', skinTone: '#c8d4e4', outfit: 'ao_trang', accessory: 'khong', aura: 'khong' }, gender: 'khac', material: this.mat });
    this.model.root.position.copy(this.home);
    this.sparks = new ParticleField({ count: 18, area: [1.2, 2.2, 1.2], center: new THREE.Vector3(0, 0.2, 0), color: '#bfe0ff', size: 0.12, speed: [0, 0.4, 0], opacity: 0.8, additive: true, drift: 0.2 });
    this.model.root.add(this.sparks.points);
    const tag = nameSprite('Linh hồn lạc lối', undefined, '#bfe0ff'); tag.position.y = 2.2; tag.scale.multiplyScalar(0.8); this.model.root.add(tag);
    parent.add(this.model.root);
  }
  get position() { return this.model.root.position; }
  release() { if (this.releasing < 0) { this.releasing = 0; this.model.setAnim('bow'); } }
  /** trả về false khi đã tan hết */
  update(dt: number, player: THREE.Vector3): boolean {
    this.t += dt;
    if (this.releasing >= 0) {
      this.releasing += dt; const k = this.releasing;
      if (k > 1.4) { this.model.root.position.y += dt * 1.6; this.mat.opacity = Math.max(0, 0.55 - (k - 1.4) * 0.3); this.mat.emissiveIntensity = 0.6 + (k - 1.4) * 2; }
      this.model.update(dt); this.sparks.update(dt, this.t);
      if (k > 3.4) { this.done = true; return false; }
      return true;
    }
    const r = 1.6; const a = this.t * 0.25;
    const x = this.home.x + Math.cos(a) * r, z = this.home.z + Math.sin(a * 1.3) * r;
    this.model.root.position.set(x, this.home.y + 0.25 + Math.sin(this.t * 1.5) * 0.12, z);
    const near = Math.hypot(player.x - x, player.z - z) < 5;
    this.model.root.rotation.y = near ? Math.atan2(player.x - x, player.z - z) : Math.atan2(-Math.sin(a), Math.cos(a));
    this.model.setAnim(near ? 'idle' : 'walk'); this.model.update(dt); this.sparks.update(dt, this.t);
    this.mat.opacity = 0.45 + Math.sin(this.t * 2) * 0.1;
    return true;
  }
  dispose() { this.model.dispose(); this.mat.dispose(); this.sparks.points.geometry.dispose(); (this.sparks.points.material as THREE.Material).dispose(); }
}

/** Lư hương nhỏ với 3 nén nhang và khói bay – tự tắt sau `life` giây. */
export class Incense {
  readonly group = new THREE.Group();
  private readonly smoke: ParticleField; private readonly embers: THREE.Mesh[] = [];
  private t = 0;
  constructor(parent: THREE.Object3D, pos: THREE.Vector3, private readonly life = 30) {
    const bronze = new THREE.MeshStandardMaterial({ color: '#7a5a2a', metalness: 0.7, roughness: 0.4 });
    const bowl = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.15, 0.16, 16), bronze); bowl.position.y = 0.08;
    const ash = new THREE.Mesh(new THREE.CircleGeometry(0.18, 16), new THREE.MeshStandardMaterial({ color: '#8a8278', roughness: 1 })); ash.rotation.x = -Math.PI / 2; ash.position.y = 0.161;
    this.group.add(bowl, ash);
    const stickM = new THREE.MeshStandardMaterial({ color: '#a8322a' }); const emberM = new THREE.MeshBasicMaterial({ color: '#ff7a2a' });
    for (let i = 0; i < 3; i++) {
      const s = new THREE.Mesh(new THREE.CylinderGeometry(0.006, 0.006, 0.42, 4), stickM); s.position.set((i - 1) * 0.05, 0.37, 0); s.rotation.z = (i - 1) * 0.12; this.group.add(s);
      const e = new THREE.Mesh(new THREE.SphereGeometry(0.012, 6, 4), emberM); e.position.set((i - 1) * 0.05 - (i - 1) * 0.025, 0.58, 0); this.group.add(e); this.embers.push(e);
    }
    this.smoke = new ParticleField({ count: 30, area: [0.6, 2.4, 0.6], center: new THREE.Vector3(0, 0.58, 0), color: '#cfc8c0', size: 0.22, speed: [0, 0.35, 0], opacity: 0.35, drift: 0.15 });
    this.group.add(this.smoke.points);
    this.group.position.copy(pos); parent.add(this.group);
  }
  update(dt: number): boolean {
    this.t += dt; this.smoke.update(dt, this.t);
    for (const [i, e] of this.embers.entries()) e.scale.setScalar(0.8 + Math.sin(this.t * 6 + i) * 0.25);
    const fade = Math.max(0, Math.min(1, (this.life - this.t) / 4));
    (this.smoke.points.material as THREE.PointsMaterial).opacity = 0.35 * fade;
    return this.t < this.life;
  }
  dispose() { this.group.traverse((o) => { const m = o as THREE.Mesh; m.geometry?.dispose(); if (m.material) (m.material as THREE.Material).dispose(); }); this.group.removeFromParent(); }
}
